import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { reviewsApi, reviewsQueryKeys } from '@/features/reviews/api';
import { useAuth } from '@/features/auth';
import { getApiErrorMessage } from '@/lib/utils';
import type { CreateReviewDto } from '@/features/reviews/types';
import type { Matter } from '../types';

interface CaseK3RatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  /** When set, rate just this one team member (Team tab per-row action). */
  person?: { id: number; name: string } | null | undefined;
  onSuccess?: () => void;
}

interface PersonRating {
  score: number | null;
  comment: string;
}

/**
 * K3 is rated **1–5** (TS Lot 1 §10.2, `K3_norm = (K3 − 1) / 4`).
 *
 * ⚠ This dialog used a 1–10 scale until 2026-08-03 while the dashboard's
 * `K3RatingDialog` used 1–5 — both writing the same `Review.rating` column. The
 * result was a column nothing could interpret: `(avg − 1) / 4` returned values
 * above 1 for a 1–10 row, and the dashboard's "K1" column divided by 2 to
 * compensate. The API now rejects anything above 5; legacy rows are converted
 * by `prisma/reviews-rescale.ts`.
 */
function getRatingColor(score: number, selected: number | null): string {
  const base =
    'h-9 w-9 rounded-full border-2 font-semibold text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ';
  const isSelected = selected === score;
  if (score === 5)
    return base + (isSelected ? 'bg-green-500 border-green-500 text-white scale-110 shadow' : 'border-green-300 text-green-700 hover:bg-green-100');
  if (score === 4)
    return base + (isSelected ? 'bg-emerald-400 border-emerald-400 text-white scale-110 shadow' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100');
  if (score === 3)
    return base + (isSelected ? 'bg-amber-400 border-amber-400 text-white scale-110 shadow' : 'border-amber-300 text-amber-700 hover:bg-amber-100');
  if (score === 2)
    return base + (isSelected ? 'bg-orange-500 border-orange-500 text-white scale-110 shadow' : 'border-orange-300 text-orange-700 hover:bg-orange-100');
  return base + (isSelected ? 'bg-red-500 border-red-500 text-white scale-110 shadow' : 'border-red-300 text-red-700 hover:bg-red-100');
}

function getPeople(matter: Matter | null, profileId?: number) {
  const seen = new Set<number>();
  const result: { id: number; name: string }[] = [];
  const add = (arr?: { id: number; name: string; avg_rating?: number | null }[]) => {
    arr?.forEach((p) => {
      if (!seen.has(p.id) && p.avg_rating == null && p.id !== profileId) {
        seen.add(p.id);
        result.push({ id: p.id, name: p.name });
      }
    });
  };
  add(matter?.partners);
  add(matter?.responsibleAttorneys);
  add(matter?.staffs);
  return result;
}

export function CaseK3RatingDialog({
  open,
  onOpenChange,
  matter,
  person,
  onSuccess,
}: CaseK3RatingDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const people = React.useMemo(
    () => (person ? [person] : getPeople(matter, user?.id)),
    [person, matter, user?.id],
  );
  const [ratings, setRatings] = React.useState<Record<number, PersonRating>>({});

  React.useEffect(() => {
    if (open) {
      const init: Record<number, PersonRating> = {};
      people.forEach((p) => { init[p.id] = { score: null, comment: '' }; });
      setRatings(init);
    }
  }, [open, people]);

  const { mutate: submitAll, isPending } = useMutation({
    mutationFn: (reviews: CreateReviewDto[]) =>
      Promise.all(reviews.map((r) => reviewsApi.create(r))),
    onSuccess: () => {
      toast.success(t('reviewCreatedSuccess'));
      queryClient.invalidateQueries({ queryKey: reviewsQueryKeys.lists() });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingReview')));
    },
  });

  const canSubmit = people.length > 0 && people.every((p) => ratings[p.id]?.score != null);

  const handleSubmit = () => {
    if (!matter || !canSubmit) return;
    const reviews: CreateReviewDto[] = people.map((p) => ({
      rating: ratings[p.id]!.score!,
      comment: ratings[p.id]!.comment,
      userId: p.id,
      matterId: matter.id,
    }));
    submitAll(reviews);
  };

  const setScore = (userId: number, score: number) =>
    setRatings((prev) => ({ ...prev, [userId]: { ...prev[userId], score } }));

  const setComment = (userId: number, comment: string) =>
    setRatings((prev) => ({ ...prev, [userId]: { ...prev[userId], comment } }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {person ? t('k3.rateMemberTitle') : t('k3.teamDialogTitle')}
          </DialogTitle>
          <DialogDescription className="text-slate-700 font-medium mt-0.5">
            {matter?.name ?? ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-0">
          {people.map((person, idx) => (
            <div key={person.id}>
              {idx > 0 && <div className="border-t border-slate-100 my-4" />}
              <div className="space-y-3 py-2">
                <p className="font-semibold text-slate-900">{person.name}</p>

                <div className="space-y-1.5">
                  <p className="text-sm text-slate-600">{t('k3.selectRating')}</p>
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {[1, 2, 3, 4, 5].map((score) => (
                      <button
                        key={score}
                        type="button"
                        onClick={() => setScore(person.id, score)}
                        className={getRatingColor(score, ratings[person.id]?.score ?? null)}
                        aria-pressed={ratings[person.id]?.score === score}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm text-slate-600">
                    {t('comment')} <span className="text-slate-400">({t('optional')})</span>
                  </label>
                  <Textarea
                    value={ratings[person.id]?.comment ?? ''}
                    onChange={(e) => setComment(person.id, e.target.value)}
                    placeholder={t('k3.commentPlaceholder')}
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending ? t('saving') : person ? t('k3.submit') : t('k3.submitAll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
